import { WorkflowResultContent } from "../workflowData/WorkflowResultContent";
import { ClientBrainProposalCard } from "../clientBrainActions/ClientBrainProposalCard";
import { sourceNames } from "../workflowData/models";
import { useRef, useState } from "react";
import type { Ref } from "react";
import { Maximize2, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "../../components/ui";
import { WorkflowOptionPicker } from "../documentation/WorkflowOptionPicker";
import { DocumentationNoteDateTime, stockholmDateTime } from "../documentation/DocumentationNoteView";
import { WorkflowEvidence, WorkflowResultBox, WorkflowResultCard } from "../documentation/WorkflowResultPresentation";
import cardStyles from "../documentation/DocumentationWorkspace.module.css";
import flowStyles from "../../components/DocumentFlow/DocumentFlow.module.css";
import { previewChoicePresentation } from "./previewChoicePresentation";
import type { BuilderWorkflow, WorkflowDraft } from "./models";
import styles from "./WorkflowBuilder.module.css";

type Props = {
    workflow: BuilderWorkflow;
    preview: NonNullable<WorkflowDraft["preview"]>;
    disabled: boolean;
    saving: boolean;
    saveSucceeded: boolean;
    savedMessage?: string;
    error?: string;
    previewRef?: Ref<HTMLElement>;
    onSelectionChange: (boxId: string, value: string) => void;
    onSaveWorkflow: () => void;
};

function PreviewTitle({ title, kind }: { title: string; kind: "trigger" | "step" }) {
    // Replace decorative emoji prefixes for display; keep the stored workflow title intact.
    const label = title.replace(/^[\s\p{Extended_Pictographic}\uFE0F\u200D]+/u, "");
    return <span className={styles.previewTitle}>
        <span className={styles.previewTitleDot} data-kind={kind} aria-hidden="true" />
        <span>{label}</span>
    </span>;
}

export function WorkflowPreviewCard({ workflow, preview, disabled, saving, saveSucceeded, savedMessage, error, previewRef, onSelectionChange, onSaveWorkflow }: Props) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const expandTrigger = useRef<HTMLButtonElement>(null);
    const expandedContent = useRef<HTMLDivElement>(null);

    const content = <>
        {preview.project_id && <p className={styles.previewClientName}><strong>{t("workflowBuilder.client")}:</strong> {preview.project_name || t("workflowBuilder.selectedClient")}</p>}
        <DocumentationNoteDateTime {...stockholmDateTime(preview.created_at)} />
        <div className={cardStyles.workflowNodeResults}>
            {workflow.boxes.map(box => {
                const result = preview.boxes.find(item => item.box_id === box.id);
                if (!result) return null;
                const choice = previewChoicePresentation(box, t("documentFlow.ibic.label"));
                const sources = result.source_references;
                const picker = box.output_type === "select" ? (
                    <WorkflowOptionPicker
                        options={choice.options}
                        value={result.selected_value ?? ""}
                        label={choice.label}
                        placeholder={t("workflowBuilder.selectOption")}
                        searchPlaceholder={t("workflowBuilder.searchOptions")}
                        emptyText={t("workflowBuilder.noMatchingOptions")}
                        disabled={disabled}
                        onSelect={value => {
                            // Keep focus in the dialog while the selected picker is disabled for generation.
                            expandedContent.current?.focus({ preventScroll: true });
                            onSelectionChange(box.id, value);
                        }}
                    />
                ) : null;
                return (
                    <WorkflowResultBox key={box.id} title={<PreviewTitle title={box.title} kind="step" />}>
                        {picker}
                        {result.client_brain_proposal && <ClientBrainProposalCard proposal={result.client_brain_proposal} disabled={disabled} />}
                        <WorkflowResultContent content={result.content} statistics={result.statistics} />
                        {(sources.length > 0 || result.evidence_spans.length > 0) && <div className={cardStyles.nodeProvenance}>
                            {sources.length > 0 && <div className={cardStyles.refChips}>
                                <strong>{t("documentFlow.basedOn")}:</strong>
                                {sources.map((source, index) => <details className={cardStyles.clientBrainReference} key={index}>
                                    <summary className={`${cardStyles.refChip} ${cardStyles.refLink}`}>
                                        {sourceNames[source.selector]}: {source.source_label}
                                    </summary>
                                    <div className={cardStyles.clientBrainReferenceDetail}>
                                        <p>{source.source_excerpt}</p>
                                        <small>{t("workflowBuilder.sourceExcerpt")} · v{source.source_version}</small>
                                    </div>
                                </details>)}
                            </div>}
                            {result.evidence_spans.length > 0 && <WorkflowEvidence spans={result.evidence_spans} />}
                        </div>}
                    </WorkflowResultBox>
                );
            })}
        </div>
    </>;

    const saveAction = <>
        {saveSucceeded && <span role="status" className={styles.saveFeedback}>{savedMessage ?? t("workflowBuilder.saved")}</span>}
        <Button variant="primary" size="sm" leftIcon={<Save size={16} />} isLoading={saving}
            disabled={disabled} onClick={() => { expandedContent.current?.focus({ preventScroll: true }); onSaveWorkflow(); }}>
            {t(saving ? "workflowBuilder.saving" : "workflowBuilder.saveWorkflow")}
        </Button>
    </>;

    return (
        <section ref={previewRef} className={styles.previewResult} aria-label={t("workflowBuilder.previewResult")}>
            {!expanded && <>
                <WorkflowResultCard title={<PreviewTitle title={workflow.title} kind="trigger" />} status={<span className={styles.previewStatus}>
                    {t("workflowBuilder.preview")}
                    <Button ref={expandTrigger} variant="ghost" size="xs" iconOnly
                        aria-label={t("workflowBuilder.expandPreview")} title={t("workflowBuilder.expandPreview")}
                        onClick={() => setExpanded(true)}><Maximize2 size={17} aria-hidden="true" /></Button>
                </span>}>
                    <div className={styles.previewCardBody}>{content}</div>
                    <div className={`${flowStyles.messageActions} ${styles.previewCardActions}`}>{saveAction}</div>
                </WorkflowResultCard>
                <details className={styles.previewInput}>
                    <summary>{t("workflowBuilder.sampleInput")}</summary>
                    <p className={styles.plainText}>{preview.input}</p>
                </details>
            </>}
            {expanded && <Modal open size="lg" className={styles.expandedPreview} onOpenChange={setExpanded}
                onCloseAutoFocus={event => { event.preventDefault(); expandTrigger.current?.focus(); }}>
                <Modal.Header className={styles.expandedPreviewHeader} title={<PreviewTitle title={workflow.title} kind="trigger" />} onClose />
                <Modal.Body className={styles.expandedPreviewBody}>
                    {disabled && !saving && <p role="status" className={styles.working}>{t("workflowBuilder.running")}</p>}
                    {error && <p role="alert" className={styles.error}>{error}</p>}
                    <div ref={expandedContent} tabIndex={-1}>{content}</div>
                </Modal.Body>
                <Modal.Footer className={styles.expandedPreviewFooter}>{saveAction}</Modal.Footer>
            </Modal>}
        </section>
    );
}
